/**
 * 통합관리자 — 기업 관리
 * ---------------------------------------------------------------------------
 * 플랫폼 운영자만 접근한다(PLATFORM_ADMIN).
 *
 * 여기서는 기업의 **현황**만 본다. 고객의 개인정보 원본은 보이지 않는다.
 * 원본이 필요하면 지원 모드를 거쳐야 한다 (docs/00-architecture.md §3.3).
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, setAccessToken, type NormalizedError } from '../../lib/api-client';
import type { SupportSession } from '../../lib/support-mode';
import { errorText } from '../../lib/queries';
import { TenantDetailModal } from './TenantDetailModal';

const { Text, Paragraph } = Typography;

interface TenantRow {
  tenantId: string;
  slug: string;
  companyName: string;
  bizRegNo: string | null;
  status: string;
  onboardedAt: string | null;
  /** 구독 중인 제품. 기업은 제품마다 별도 구독을 갖는다 */
  products: Array<{
    productCode: string;
    productName: string;
    planCode: string;
    planName: string;
    status: string;
    units: number;
  }>;
  subscriptionCount: number;
  nextPeriodEnd: string | null;
  /** 해지한 기업만. 이 날짜가 지나면 배치가 파기한다 */
  purgeScheduledAt: string | null;
  userCount: number;
  activeUserCount: number;
  departmentCount: number;
  createdAt: string;
}

/** 파기 미리보기. 무엇이 몇 건 지워지는지 먼저 받아 본다. */
interface PurgeCheck {
  status: string;
  purgeScheduledAt: string | null;
  dueNow: boolean;
  lines: Array<{ table: string; rows: number }>;
  totalRows: number;
  files: { count: number; bytes: number };
}

interface PurgeResult {
  companyName: string;
  totalRows: number;
  filesDeleted: number;
  filesFailed: number;
}

interface Summary {
  tenantTotal: number;
  tenantActive: number;
  tenantSuspended: number;
  userTotal: number;
  userActive: number;
}

const STATUS_META: Record<string, { text: string; color: string }> = {
  PENDING: { text: '가입대기', color: 'default' },
  ACTIVE: { text: '정상', color: 'green' },
  GRACE: { text: '결제유예', color: 'orange' },
  SUSPENDED: { text: '정지', color: 'red' },
  TERMINATED: { text: '해지', color: 'default' },
};

export default function AdminTenantPage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [createForm] = Form.useForm();
  const [supportForm] = Form.useForm();

  const [keyword, setKeyword] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  /** 줄을 눌러 연 기업 상세. 비어 있으면 닫혀 있다 */
  const [detailTarget, setDetailTarget] = useState<TenantRow | null>(null);
  const [supportTarget, setSupportTarget] = useState<TenantRow | null>(null);
  const [entering, setEntering] = useState(false);
  const [supportError, setSupportError] = useState<string | null>(null);

  const summary = useQuery<Summary, NormalizedError>({
    queryKey: ['admin', 'summary'],
    queryFn: async () => (await apiClient.get<Summary>('/admin/tenants/summary')).data,
  });

  const tenants = useQuery<TenantRow[], NormalizedError>({
    queryKey: ['admin', 'tenants', submitted, status],
    queryFn: async () =>
      (
        await apiClient.get<TenantRow[]>('/admin/tenants', {
          params: { keyword: submitted || undefined, status },
        })
      ).data,
  });

  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ['admin'] });
  };

  const createTenant = async () => {
    const values = await createForm.validateFields().catch(() => null);
    if (!values) return;

    setCreating(true);
    setCreateError(null);
    try {
      const { data } = await apiClient.post<{ tenantId: string; inviteUrl: string }>(
        '/admin/tenants',
        values,
      );
      setCreateOpen(false);
      createForm.resetFields();
      // 초대 링크는 이 시점에만 볼 수 있다. 모달로 크게 보여준다.
      setInviteUrl(`${window.location.origin}${data.inviteUrl}`);
      await refresh();
    } catch (err) {
      setCreateError(errorText(err as NormalizedError));
    } finally {
      setCreating(false);
    }
  };

  /**
   * 지원 모드 진입.
   *
   * 성공하면 Access Token 이 **대상 기업 컨텍스트**로 바뀐다. 그때부터
   * 구성원·조직·설정 화면이 그대로 그 기업의 데이터를 다룬다.
   *
   * 화면 안에서 상태만 갈아 끼우지 않고 **주소를 새로 연다.** 지원 모드에
   * 들어가면 통합관리자 라우트가 통째로 사라지는데, 같은 틱에 상태와 경로가
   * 함께 바뀌면 중간 상태가 생겨 엉뚱한 화면으로 튀거나(실제로 /app/users 가
   * 아니라 /app/dashboard 로 갔다) 아예 백지가 된다. 서버가 쿠키를 심어
   * 두었으므로 다시 띄우면 restoreSession() 이 지원 세션으로 복구한다.
   */
  const enterSupport = async () => {
    const target = supportTarget;
    if (!target) return;

    const values = await supportForm.validateFields().catch(() => null);
    if (!values) return;

    setEntering(true);
    setSupportError(null);
    try {
      await apiClient.post<SupportSession & { accessToken: string }>(
        `/admin/tenants/${target.tenantId}/support`,
        { reason: values.reason.trim(), minutes: values.minutes },
      );

      // 플랫폼 데이터가 캐시에 남아 고객사 화면에 섞이지 않게 비운다.
      // (새로고침이 어차피 비우지만, 전환 중 한 프레임이라도 섞이면 안 된다.)
      setAccessToken(null);
      client.clear();
      window.location.assign('/app/users');
    } catch (err) {
      setSupportError(errorText(err as NormalizedError));
      setEntering(false);
    }
  };

  const changeStatus = (row: TenantRow, next: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED') => {
    let reason = '';
    const labels = { ACTIVE: '정상으로 전환', SUSPENDED: '정지', TERMINATED: '해지' };

    Modal.confirm({
      title: `${row.companyName} — ${labels[next]}`,
      width: 520,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {next !== 'ACTIVE' && (
            <Alert
              type={next === 'TERMINATED' ? 'error' : 'warning'}
              showIcon
              message={
                next === 'TERMINATED'
                  ? '해지하면 30일 뒤 데이터 파기 대상이 됩니다'
                  : `소속 구성원 ${row.activeUserCount}명이 로그인할 수 없게 됩니다`
              }
              description={
                next === 'TERMINATED'
                  ? '되돌리려면 유예기간 안에 다시 정상으로 전환해야 합니다.'
                  : '되돌릴 수 있는 조치입니다.'
              }
            />
          )}
          <Text type="secondary">변경 사유는 감사로그에 기록됩니다.</Text>
          <Input.TextArea
            rows={2}
            placeholder="예: 결제 미납 3개월 경과"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      okText: labels[next],
      okButtonProps: { danger: next !== 'ACTIVE' },
      cancelText: '취소',
      async onOk() {
        if (!reason.trim()) {
          message.error('변경 사유를 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        try {
          await apiClient.patch(`/admin/tenants/${row.tenantId}/status`, {
            status: next,
            reason: reason.trim(),
          });
          message.success(`${row.companyName} 상태를 변경했습니다.`);
          await refresh();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          throw err;
        }
      },
    });
  };

  /**
   * 완전 삭제.
   *
   * 되돌릴 수 없으므로 두 번 확인한다 — 먼저 **무엇이 몇 건 지워지는지**
   * 서버에서 받아 보여 주고, 그 다음 상호를 직접 입력하게 한다. 사유는
   * 플랫폼 감사로그에 남는다(그 기업의 감사로그는 함께 지워진다).
   */
  const purge = async (row: TenantRow) => {
    let check: PurgeCheck;
    try {
      check = (await apiClient.get<PurgeCheck>(`/admin/tenants/${row.tenantId}/purge-check`)).data;
    } catch (err) {
      message.error(errorText(err as NormalizedError));
      return;
    }

    let reason = '';
    let typed = '';

    Modal.confirm({
      title: `${row.companyName} — 완전 삭제`,
      width: 640,
      icon: null,
      content: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="error"
            showIcon
            message="되돌릴 수 없습니다"
            description={
              <>
                구성원 계정과 개인정보, 일지·첨부·과금 기록, 그리고 <b>암호화 키</b>까지 지웁니다.
                키를 지우면 백업에서 되살려도 내용을 읽을 수 없습니다.
              </>
            }
          />

          {check.status !== 'TERMINATED' && (
            <Alert
              type="warning"
              showIcon
              message="해지 상태가 아닙니다"
              description="원래는 해지 후 30일이 지나야 자동으로 파기됩니다. 여기서 지우면 유예기간을 건너뜁니다."
            />
          )}

          <div>
            <Text strong>지워지는 것</Text>
            <div style={{ maxHeight: 220, overflow: 'auto', marginTop: 8 }}>
              <Table
                size="small"
                rowKey="table"
                pagination={false}
                dataSource={check.lines}
                columns={[
                  { title: '표', dataIndex: 'table', key: 'table' },
                  {
                    title: '건수',
                    dataIndex: 'rows',
                    key: 'rows',
                    width: 90,
                    align: 'right',
                    render: (v: number) => v.toLocaleString('ko-KR'),
                  },
                ]}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              합계 {check.totalRows.toLocaleString('ko-KR')}건 · 첨부 파일 {check.files.count}개
            </Text>
          </div>

          <div>
            <Text type="secondary">파기 사유 (10자 이상, 감사로그에 남습니다)</Text>
            <Input.TextArea
              rows={2}
              placeholder="예: 내부 테스트용으로 만든 기업. 오픈 전 정리."
              onChange={(e) => {
                reason = e.target.value;
              }}
            />
          </div>

          <div>
            <Text type="secondary">
              확인을 위해 상호 <Text code>{row.companyName}</Text> 를 그대로 입력하세요
            </Text>
            <Input
              placeholder={row.companyName}
              onChange={(e) => {
                typed = e.target.value;
              }}
            />
          </div>
        </Space>
      ),
      okText: '완전 삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      async onOk() {
        if (reason.trim().length < 10) {
          message.error('파기 사유를 10자 이상 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        if (typed.trim() !== row.companyName) {
          message.error('상호가 일치하지 않습니다.');
          return Promise.reject(new Error('confirm mismatch'));
        }
        try {
          const { data } = await apiClient.delete<PurgeResult>(`/admin/tenants/${row.tenantId}`, {
            data: { reason: reason.trim(), force: true },
          });
          message.success(
            `${data.companyName} 파기 완료 — ${data.totalRows.toLocaleString('ko-KR')}건, 파일 ${data.filesDeleted}개`,
          );
          await refresh();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          throw err;
        }
      },
    });
  };

  const columns: ColumnsType<TenantRow> = [
    {
      title: '기업',
      key: 'company',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <strong>{row.companyName}</strong>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.slug}
            {row.bizRegNo ? ` · ${row.bizRegNo}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: string, row) => {
        const m = STATUS_META[v] ?? { text: v, color: 'default' };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={m.color}>{m.text}</Tag>
            {/*
              해지하면 「30일 뒤 파기」 라고 안내해 놓고 정작 언제인지는 못
              보여 주고 있었다. 날짜를 적는다.
            */}
            {v === 'TERMINATED' && row.purgeScheduledAt && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {new Date(row.purgeScheduledAt) <= new Date() ? '파기 대기' : '파기 예정'}{' '}
                {new Date(row.purgeScheduledAt).toLocaleDateString('ko-KR')}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      // 기업은 제품마다 따로 구독한다. 요금제 하나로 요약할 수 없다.
      title: '구독 제품',
      key: 'products',
      width: 220,
      render: (_, row) =>
        (row.products ?? []).length === 0 ? (
          <Text type="secondary">구독 없음</Text>
        ) : (
          <Space size={4} wrap>
            {row.products.map((p) => (
              <Tooltip key={p.productCode} title={`${p.planName} · ${p.status}`}>
                <Tag color={p.status === 'ACTIVE' ? 'blue' : 'orange'}>{p.productName}</Tag>
              </Tooltip>
            ))}
          </Space>
        ),
    },
    {
      title: '구성원',
      key: 'users',
      width: 110,
      render: (_, row) => (
        <span>
          {row.activeUserCount} / {row.userCount}명
        </span>
      ),
    },
    { title: '부서', dataIndex: 'departmentCount', key: 'dept', width: 80 },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('ko-KR') : '-'),
    },
    {
      title: '관리',
      key: 'actions',
      width: 260,
      fixed: 'right',
      render: (_, row) =>
        row.slug === 'system' ? (
          <Text type="secondary">시스템</Text>
        ) : (
          <Space size={4}>
            {/* 해지된 기업은 데이터 파기 대상이므로 들어갈 이유가 없다. */}
            {row.status !== 'TERMINATED' && (
              <Button size="small" type="link" onClick={() => setSupportTarget(row)}>
                지원 모드
              </Button>
            )}
            {row.status !== 'ACTIVE' && (
              <Button size="small" onClick={() => changeStatus(row, 'ACTIVE')}>
                정상 전환
              </Button>
            )}
            {row.status === 'ACTIVE' && (
              <Button size="small" danger onClick={() => changeStatus(row, 'SUSPENDED')}>
                정지
              </Button>
            )}
            {row.status !== 'TERMINATED' && (
              <Button size="small" danger type="text" onClick={() => changeStatus(row, 'TERMINATED')}>
                해지
              </Button>
            )}
            {/*
              완전 삭제는 되돌릴 수 없다. 평소에는 눈에 띄지 않게 두고,
              누르면 무엇이 지워지는지 먼저 보여 준다.
            */}
            <Tooltip title="데이터와 암호화 키까지 모두 지웁니다. 되돌릴 수 없습니다.">
              <Button size="small" danger type="text" onClick={() => void purge(row)}>
                완전 삭제
              </Button>
            </Tooltip>
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="전체 기업" value={summary.data?.tenantTotal ?? 0} suffix="곳" />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="정상 운영"
              value={summary.data?.tenantActive ?? 0}
              suffix="곳"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="정지"
              value={summary.data?.tenantSuspended ?? 0}
              suffix="곳"
              valueStyle={{ color: summary.data?.tenantSuspended ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="활성 사용자"
              value={summary.data?.userActive ?? 0}
              suffix={`/ ${summary.data?.userTotal ?? 0}명`}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Space wrap size="middle">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={() => setSubmitted(keyword.trim())}
            placeholder="상호 · 식별자 · 사업자번호"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 260 }}
          />
          <Select
            value={status}
            onChange={setStatus}
            allowClear
            placeholder="상태"
            style={{ width: 130 }}
            options={Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.text }))}
          />
          <Button type="primary" onClick={() => setSubmitted(keyword.trim())}>
            검색
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void refresh()} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            기업 등록
          </Button>
        </Space>
      </Card>

      {tenants.isError && <Alert type="error" showIcon message={errorText(tenants.error)} />}

      <Table<TenantRow>
        rowKey="tenantId"
        columns={columns}
        dataSource={tenants.data ?? []}
        loading={tenants.isLoading}
        size="middle"
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20, showTotal: (t) => `총 ${t}곳` }}
        locale={{ emptyText: '등록된 기업이 없습니다.' }}
        /*
          줄을 누르면 상세가 열린다.
          「관리」 칸의 단추(지원 모드·파기 등)를 누를 때는 열리지 않아야
          한다 — 단추를 누른 것이지 줄을 누른 것이 아니다.
        */
        onRow={(row) => ({
          style: { cursor: 'pointer' },
          onClick: (event) => {
            const el = event.target as HTMLElement;
            if (el.closest('button, a, .ant-dropdown-trigger, .ant-popover')) return;
            setDetailTarget(row);
          },
        })}
      />

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        줄을 누르면 기업 상세가 열립니다. 목록은 집계값만 보여주며, 고객의 구성원 정보 등
        개인정보 원본은 지원 모드를 거쳐야 접근할 수 있고 모든 접근이 감사로그에 기록됩니다.
      </Paragraph>

      {/* 기업 상세 — 줄을 누르면 열린다 */}
      <TenantDetailModal
        tenant={detailTarget}
        onClose={() => setDetailTarget(null)}
        onChanged={() => void tenants.refetch()}
      />

      {/* 지원 모드 진입 */}
      <Modal
        open={Boolean(supportTarget)}
        title={`지원 모드 — ${supportTarget?.companyName ?? ''}`}
        okText="진입"
        cancelText="취소"
        okButtonProps={{ danger: true }}
        confirmLoading={entering}
        onCancel={() => {
          setSupportTarget(null);
          setSupportError(null);
        }}
        onOk={enterSupport}
        width={540}
        // destroyOnHidden 과 함께 쓰면 Form 이 열릴 때 연결된다.
        // 이게 없으면 "useForm is not connected to any Form element" 경고가 난다.
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) supportForm.resetFields();
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {supportError && <Alert type="error" showIcon message={supportError} />}
          <Alert
            type="warning"
            showIcon
            message="고객사의 개인정보에 직접 접근합니다"
            description={
              <span>
                진입하면 구성원·조직·설정 화면이 {supportTarget?.companyName} 의 실제 데이터를
                다룹니다. 세션 중 모든 조회와 변경이 사유와 함께 감사로그에 남고, 고객사에
                공개될 수 있습니다. 시간이 지나면 자동으로 종료됩니다.
              </span>
            }
          />
          <Form form={supportForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="reason"
              label="접근 사유"
              rules={[
                { required: true, message: '접근 사유를 입력하세요' },
                { min: 10, message: '나중에 설명할 수 있도록 10자 이상 구체적으로 적어 주세요' },
              ]}
              extra="예: 문의 #1042 — 부서 이동 후 권한이 반영되지 않는 현상 확인"
            >
              <Input.TextArea rows={3} maxLength={500} showCount />
            </Form.Item>
            <Form.Item
              name="minutes"
              label="유효 시간(분)"
              initialValue={60}
              rules={[{ required: true, message: '유효 시간을 입력하세요' }]}
              extra="필요한 만큼만 지정하세요. 최대 240분이며 종료 버튼으로 언제든 끝낼 수 있습니다."
            >
              <InputNumber min={5} max={240} step={5} style={{ width: 160 }} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 기업 등록 */}
      <Modal
        open={createOpen}
        title="기업 등록"
        okText="등록"
        cancelText="취소"
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        onOk={createTenant}
        width={540}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {createError && <Alert type="error" showIcon message={createError} />}
          <Alert
            type="info"
            showIcon
            message="등록하면 자동으로 준비되는 것들"
            description="개인정보 암호화 키, 보안 정책 기본값, 파일 정책, 구독, 최상위 부서, 기업 관리자 계정이 함께 생성됩니다."
          />
          <Form form={createForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="companyName"
              label="상호"
              rules={[{ required: true, message: '상호를 입력하세요' }]}
            >
              <Input placeholder="(주)에이콤" />
            </Form.Item>
            <Form.Item
              name="slug"
              label="기업 식별자"
              rules={[
                { required: true, message: '식별자를 입력하세요' },
                {
                  pattern: /^[a-z][a-z0-9-]{2,39}$/,
                  message: '영소문자로 시작하는 3~40자 (영소문자·숫자·하이픈)',
                },
              ]}
              extra="로그인 URL 과 기업 구분에 사용됩니다. 등록 후 변경할 수 없습니다."
            >
              <Input placeholder="acme" />
            </Form.Item>
            <Space size="middle" style={{ display: 'flex' }}>
              <Form.Item
                name="bizRegNo"
                label="사업자등록번호"
                rules={[{ pattern: /^\d{10}$/, message: '숫자 10자리' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="1234567890" maxLength={10} />
              </Form.Item>
              <Form.Item
                name="planCode"
                label="요금제"
                initialValue="FREE"
                style={{ flex: 1 }}
              >
                <Select
                  options={[
                    { value: 'FREE', label: '무료' },
                    { value: 'STANDARD', label: '스탠다드' },
                    { value: 'BUSINESS', label: '비즈니스' },
                  ]}
                />
              </Form.Item>
            </Space>
            <Form.Item
              name="adminName"
              label="기업 관리자 이름"
              rules={[{ required: true, message: '관리자 이름을 입력하세요' }]}
            >
              <Input placeholder="홍길동" />
            </Form.Item>
            <Form.Item
              name="adminEmail"
              label="기업 관리자 이메일"
              rules={[
                { required: true, message: '관리자 이메일을 입력하세요' },
                { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
              ]}
              extra="이 계정이 TENANT_OWNER 로 지정되며, 초대 링크가 발급됩니다."
            >
              <Input placeholder="admin@acme.co.kr" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 초대 링크 */}
      <Modal
        open={Boolean(inviteUrl)}
        title="기업을 등록했습니다"
        onCancel={() => setInviteUrl(null)}
        onOk={() => setInviteUrl(null)}
        okText="확인"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="이 링크는 지금만 볼 수 있습니다"
            description="기업 관리자에게 전달하세요. 7일간 유효하며, 본인이 비밀번호를 설정합니다. 분실하면 관리자 계정을 다시 만들어야 합니다."
          />
          <Input.TextArea readOnly value={inviteUrl ?? ''} autoSize={{ minRows: 2 }} />
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(inviteUrl ?? '');
              message.success('링크를 복사했습니다.');
            }}
          >
            링크 복사
          </Button>
        </Space>
      </Modal>
    </Space>
  );
}
